import { z } from 'zod';

export const invoicingModeSchema = z.enum(['relaxed_open', 'relaxed_closed', 'strict']);

export const securityGroupFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255, 'Name must be 255 characters or fewer'),
  owner_dealer_id: z.string().uuid('Owner dealer must be a valid UUID'),
  invoicing_mode: invoicingModeSchema,
});

// Edit form: name and invoicing mode are mutable post-create.
export const securityGroupEditFormSchema = securityGroupFormSchema.pick({ name: true, invoicing_mode: true });

// Mirrors `AttachUsersRequest::rules()` — `default_permissions` is optional
// on the request body, but when present it must include the FULL triple
// (`required_array_keys:can_quote,can_invoice,can_sell` + `required_with`).
// A partial schema would let UI code build a `{can_quote: true}` payload that
// 422s in production. The triple itself is required-when-present; callers
// model the optional-on-request-body part by using `.optional()` at the
// composition site (e.g. on the parent `attachUsersPayloadSchema`).
export const attachUsersDefaultPermissionsSchema = z.object({
  can_quote: z.boolean(),
  can_invoice: z.boolean(),
  can_sell: z.boolean(),
});

export const groupPermissionsSchema = z.object({
  can_quote: z.boolean(),
  can_invoice: z.boolean(),
  can_sell: z.boolean(),
});

// Each dealer UUID key carries a nested `permissions` triple; a flat triple
// under a dealer key would 422 in the backend request.
export const dealerOverrideSchema = z.object({
  permissions: groupPermissionsSchema,
});

const uuidSchema = z.string().uuid();

const userPermissionsKey = z.string().refine(
  (k) => k === 'group' || uuidSchema.safeParse(k).success,
  { message: "Key must be 'group' or a dealer UUID" },
);

// `z.record` cannot pick a value schema per key, so we accept either shape at
// the type level and use `superRefine` to assert the correct shape based on
// whether the key is the literal `group` or a dealer UUID.
const userPermissionsEntrySchema = z.union([groupPermissionsSchema, dealerOverrideSchema]);

export const userPermissionsSchema = z.object({
  user_permissions: z
    .record(userPermissionsKey, userPermissionsEntrySchema)
    .superRefine((map, ctx) => {
      const entries = Object.entries(map);
      if (entries.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least one permissions entry required',
          path: [],
        });
        return;
      }

      for (const [key, value] of entries) {
        if (key === 'group') {
          const parsed = groupPermissionsSchema.safeParse(value);
          if (!parsed.success) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Entry 'group' must be a flat permissions triple",
              path: [key],
            });
          }
        } else {
          const parsed = dealerOverrideSchema.safeParse(value);
          if (!parsed.success) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Dealer entries must be { permissions: {can_quote, can_invoice, can_sell} }',
              path: [key],
            });
          }
        }
      }
    }),
});

export type SecurityGroupFormInput = z.input<typeof securityGroupFormSchema>;
export type SecurityGroupFormOutput = z.output<typeof securityGroupFormSchema>;
export type SecurityGroupEditFormInput = z.input<typeof securityGroupEditFormSchema>;
export type SecurityGroupEditFormOutput = z.output<typeof securityGroupEditFormSchema>;
export type AttachUsersDefaultPermissions = z.output<typeof attachUsersDefaultPermissionsSchema>;
export type UserPermissionsFormInput = z.input<typeof userPermissionsSchema>;
